import { baseApi } from '@/lib/rtkQuery/baseApi';

import {
	FetchDataCaptureCategoriesResponse,
	FetchDataPointsResponse,
	FetchDataCaptureCategoryTypeResponse,
	FetchDataCaptureCategoryTypeRequest,
	CreateDataCapturePointBody,
	DeleteDataCapturePointRequest,
	FetchPersonDataCaptureStudiesRequest,
	FetchPersonDataCaptureStudiesResponse,
} from '../types';

export const patientDataCaptureApi = baseApi
	.enhanceEndpoints({ addTagTypes: ['categoriesList', 'captureList'] })
	.injectEndpoints({
		endpoints: (build) => ({
			fetchDataCaptureCategories: build.query<
				FetchDataCaptureCategoriesResponse,
				{ patient_identifier: string; capturable_only?: boolean }
			>({
				query: ({ patient_identifier, capturable_only }) => {
					// `capturable_only=true` limits the response to categories the patient
					// can capture right now — used by the data entry form's category
					// dropdown. Omit it elsewhere (e.g. tabs) so every category is returned.
					const capturableParam = capturable_only ? '&capturable_only=true' : '';
					return {
						url: `v1/data-capture/categories/?patient_id=${patient_identifier}${capturableParam}`,
						method: 'GET',
					};
				},
				providesTags: ['categoriesList'],
			}),
			fetchDataCaptureCategoryType: build.query<
				FetchDataCaptureCategoryTypeResponse,
				FetchDataCaptureCategoryTypeRequest
			>({
				query: ({ categoryTypeId }) => {
					return {
						url: `v1/data-capture/type/${categoryTypeId}/`,
						method: 'GET',
					};
				},
			}),
			fetchDataPoints: build.query<FetchDataPointsResponse, { patient_identifier: string }>({
				query: ({ patient_identifier }) => {
					return {
						url: `v1/data-capture/point/?patient_id=${patient_identifier}&all=true&include_deleted=true`,
						method: 'GET',
					};
				},
				transformResponse: (response: FetchDataPointsResponse) => {
					return Array.isArray(response)
						? { results: response, total: response.length }
						: response;
				},
				providesTags: ['captureList'],
			}),
			createDataPoint: build.mutation<void, CreateDataCapturePointBody>({
				query: (body) => {
					return {
						url: 'v1/data-capture/point/',
						method: 'POST',
						body,
					};
				},
				invalidatesTags: ['categoriesList', 'captureList'],
			}),
			deleteDataPoint: build.mutation<void, DeleteDataCapturePointRequest>({
				query: ({ note, dataPointId }) => {
					return {
						url: `v1/data-capture/point/${dataPointId}/delete/`,
						method: 'POST',
						body: { note },
					};
				},
				invalidatesTags: ['categoriesList', 'captureList'],
			}),
			fetchPersonDataCaptureStudies: build.query<
				FetchPersonDataCaptureStudiesResponse,
				FetchPersonDataCaptureStudiesRequest
			>({
				query: ({ personId }) => {
					return {
						url: `/v1/data-capture/studies/?person_id=${personId}`,
						method: 'GET',
					};
				},
			}),
		}),
	});

export const {
	useFetchDataCaptureCategoriesQuery,
	useFetchDataCaptureCategoryTypeQuery,
	useFetchDataPointsQuery,
	useCreateDataPointMutation,
	useDeleteDataPointMutation,
	useFetchPersonDataCaptureStudiesQuery,
} = patientDataCaptureApi;
